import { yupResolver } from '@hookform/resolvers/yup'
import { InputAdornment, Stack } from '@mui/material'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useSelectPricingMatrix } from '../../../hooks/useSelectPricingMatrix/useSelectPricingMatrix'
import { LabelsInventoryParts } from '../../../models/Labels'
import { InventoryContext } from '../../../providers/InventoryProvider/InventoryProvider'
import {
  useCreateInventoryPartMutation,
  useUpdateInventoryPartMutation
} from '../../../redux/api'
import { PartTaxonomy } from '../../../sdk'
import { InventoryPartEntry } from '../../../sdk/models/InventoryPartEntry'
import {
  InventoryPartInfo,
  InventoryPartInfoUnitOfMeasureEnum
} from '../../../sdk/models/InventoryPartInfo'
import { formatCurrency } from '../../../utils'
import { NONE_ID } from '../../../utils/convertEmptyStringToUndefined/convertEmptyStringToUndefined'
import {
  ControlledMuiCheckboxSingle,
  ControlledMuiSelect,
  ControlledMuiTextField
} from '../../FormField'
import { PartTaxonomyAutoComplete } from '../../PartTaxonomyAutoComplete/PartTaxonomyAutoComplete'
import { DEFAULT_INVENTORY_PART } from '../defaultValues'
import { VALIDATE_INVENTORY_PART } from '../validation'
import {
  BrandCreatableAutoComplete,
  ModifiedBrand
} from './BrandCreatableAutoComplete'
import {
  LocationCreatableAutoComplete,
  ModifiedOption
} from './LocationCreatableAutoComplete'

function getDummySelectedBrandFromInventoryPart(
  defaultValues: InventoryPartEntry
): ModifiedBrand {
  return {
    id: undefined,
    brandId: '',
    brandName: defaultValues.info!.brand,
    displayName:
      defaultValues.info!.brandDisplayName || defaultValues.info!.brand
  }
}

export type ModifiedInventoryInfo = Omit<
  InventoryPartInfo,
  'suggestedPricePerUnit' | 'unitOfMeasure'
> & {
  suggestedPricePerUnit: number | ''
  unitOfMeasure: InventoryPartInfoUnitOfMeasureEnum | ''
}

export const EditInventoryForm = ({
  formId,
  defaultValues,
  submitCallback
}: {
  formId: string
  defaultValues: InventoryPartEntry | undefined
  submitCallback: () => void
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { view } = useContext(InventoryContext)
  const [createInventoryPart] = useCreateInventoryPartMutation()
  const [updateInventoryPart] = useUpdateInventoryPartMutation()

  const [selectedBrand, setSelectedBrand] = useState<ModifiedBrand | undefined>(
    defaultValues
      ? getDummySelectedBrandFromInventoryPart(defaultValues)
      : undefined
  )
  const [selectedLocation, setSelectedLocation] = useState<
    ModifiedOption | undefined
  >(
    defaultValues?.info!.location
      ? {
          name: defaultValues?.info!.location
        }
      : undefined
  )

  const [selectedTaxonomy, setSelectedTaxonomy] = React.useState<
    PartTaxonomy | undefined
  >(defaultValues?.info!.partTaxonomy)

  const { handleSubmit, control, watch, setValue } =
    useForm<ModifiedInventoryInfo>({
      mode: 'onBlur',
      defaultValues: defaultValues
        ? {
            ...defaultValues.info!,
            partsMatrix: defaultValues.info!.suggestedPricePerUnit
              ? undefined
              : defaultValues.info!.partsMatrix,
            suggestedPricePerUnit: defaultValues.info!.suggestedPricePerUnit
              ? defaultValues.info!.suggestedPricePerUnit
              : ''
          }
        : {
            ...DEFAULT_INVENTORY_PART,
            shop: shopId,
            isFluids: view === 'fluids',
            stocked: view === 'stocked' || view === 'fluids'
          },
      resolver: yupResolver(VALIDATE_INVENTORY_PART)
    })

  const {
    selectedPartsMatrixId,
    SelectPriceLevelComponent,
    SelectPartsMatrixComponent
  } = useSelectPricingMatrix(
    '',
    '',
    defaultValues?.info!.suggestedPricePerUnit
      ? NONE_ID
      : defaultValues?.info!.partsMatrix || '',
    { partTaxonomy: selectedTaxonomy }
  )

  useEffect(() => {
    setValue('partsMatrix', selectedPartsMatrixId)
  }, [selectedPartsMatrixId, setValue])

  const isFluidsInput = watch('isFluids')

  const onSubmit: SubmitHandler<ModifiedInventoryInfo> = useCallback(
    async (data) => {
      if (!selectedBrand) {
        return
      }
      const partsMatrix = data.partsMatrix
      const suggestedPricePerUnit = data.partsMatrix
        ? undefined
        : (data.suggestedPricePerUnit! as number)
      const unitOfMeasure =
        suggestedPricePerUnit === undefined
          ? undefined
          : (data.unitOfMeasure! as InventoryPartInfoUnitOfMeasureEnum)
      const brand = selectedBrand.brandName || undefined
      const brandDisplayName =
        selectedBrand.inputValue || selectedBrand.displayName || undefined
      const size = data.size || undefined
      const location =
        selectedLocation?.inputValue || selectedLocation?.name || undefined
      const info = {
        ...data,
        brand,
        brandDisplayName,
        suggestedPricePerUnit,
        partsMatrix,
        unitOfMeasure,
        size,
        location
      }
      defaultValues
        ? updateInventoryPart({
            authorization: accessToken,
            inventoryPartEntry: {
              id: defaultValues.id!,
              info
            }
          })
        : createInventoryPart({
            authorization: accessToken,
            inventoryPartInfo: info
          })
      submitCallback()
    },
    [
      accessToken,
      selectedBrand,
      createInventoryPart,
      defaultValues,
      submitCallback,
      updateInventoryPart,
      selectedLocation
    ]
  )

  // Assumptions: if new part isFluids is true, we only allow to set price with fluids unit
  // if new part isFluids is false, we only allow to set price with discrete unit
  const unitOptions = useMemo(() => {
    const fluidsOptions = Object.values(InventoryPartInfoUnitOfMeasureEnum)
      .map((u) => ({
        id: u,
        description: u
      }))
      .filter(
        (u) =>
          u.id !== (InventoryPartInfoUnitOfMeasureEnum.DiscreteUnit as string)
      )
    const nonFluidsOptions = [
      {
        id: InventoryPartInfoUnitOfMeasureEnum.DiscreteUnit,
        description: InventoryPartInfoUnitOfMeasureEnum.DiscreteUnit
      }
    ]
    if (isFluidsInput) {
      return fluidsOptions
    } else {
      return nonFluidsOptions
    }
  }, [isFluidsInput])

  return (
    <form
      id={formId}
      onSubmit={handleSubmit((data) =>
        onSubmit({ ...data, partTaxonomy: selectedTaxonomy })
      )}>
      <Stack spacing={2} mt={2}>
        <Stack direction={'row'} spacing={1}>
          <ControlledMuiTextField
            variant="standard"
            control={control}
            name="name"
            label={LabelsInventoryParts.name}
            fullWidth
          />
          <ControlledMuiTextField
            variant="standard"
            control={control}
            name="partNumber"
            label={LabelsInventoryParts.partNumber}
            fullWidth
          />
        </Stack>
        <BrandCreatableAutoComplete
          selectedBrand={selectedBrand}
          setSelectedBrand={setSelectedBrand}
          defaultValues={
            defaultValues
              ? getDummySelectedBrandFromInventoryPart(defaultValues)
              : undefined
          }
        />
        <Stack direction={'row'} spacing={1}>
          {watch('listCost') && (
            <ControlledMuiTextField
              name="listCost"
              control={control}
              label={LabelsInventoryParts.listCost}
              type="number"
              overrideValue={
                watch('listCost') !== undefined
                  ? formatCurrency(watch('listCost'), false)
                  : ''
              }
              variant="standard"
              disabled
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                )
              }}
            />
          )}
        </Stack>
        <Stack direction={'row'} spacing={1}>
          <ControlledMuiTextField
            variant="standard"
            control={control}
            name="size"
            label={LabelsInventoryParts.size}
            fullWidth
          />
          <LocationCreatableAutoComplete
            {...{ selectedLocation, setSelectedLocation, defaultValues }}
          />
        </Stack>
        <Stack direction={'row'} spacing={1}>
          <SelectPriceLevelComponent variant="standard" />
          <SelectPartsMatrixComponent variant="standard" />
          {!selectedPartsMatrixId && (
            <>
              <ControlledMuiTextField
                variant="standard"
                control={control}
                name="suggestedPricePerUnit"
                label={LabelsInventoryParts.suggestedPricePerUnit}
                fullWidth
              />
              <ControlledMuiSelect
                name="unitOfMeasure"
                variant="standard"
                control={control}
                label={LabelsInventoryParts.unitOfMeasure}
                options={unitOptions}
                fullWidth
              />
            </>
          )}
        </Stack>
        <PartTaxonomyAutoComplete
          {...{ selectedTaxonomy, setSelectedTaxonomy }}
        />
        <ControlledMuiTextField
          control={control}
          name="description"
          label={LabelsInventoryParts.description}
          multiline
          minRows={3}
        />

        <Stack direction={'row'} spacing={1}>
          <ControlledMuiCheckboxSingle
            control={control}
            name="stocked"
            label={LabelsInventoryParts.stocked}
          />
          <ControlledMuiCheckboxSingle
            control={control}
            name="isFluids"
            label={LabelsInventoryParts.isFluids}
            // isFluids inventory part is disallowed to change to non-fluids, because non-fluids inventory part requires all instance to have discrete unit
            disabled={defaultValues && defaultValues.info!.isFluids}
          />
        </Stack>
      </Stack>
    </form>
  )
}
